import axios from "axios";
import { notification } from "antd";
import { clearAuthToken, getAuthTokenExpiration, getRealestatesFromAuthToken, loadAuthTokenFromLocalStorage, saveAuthTokenInLocalStorage } from "./jwt_helper.js";

export { loadAuthTokenFromLocalStorage, saveAuthTokenInLocalStorage } from "./jwt_helper.js";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

// Start check if we should refresh token when 30 minutes remain until current token expiry
const TOKEN_REFRESH_THRESHOLD_MILLISECONDS = 30 * 60 * 1000;
// When checking if we should refresh token, look for activity in the last 15 minutes.
const RECENT_ACTIVITY_WINDOW_MILLISECONDS = 15 * 60 * 1000;
// Activity updates are capped at 1 per 5 seconds.
const ACTIVITY_THROTTLE_MILLISECONDS = 5 * 1000;

let lastActivityTimestamp = Date.now();
let lastRecordedActivityTimestamp = 0;
let refreshTokenPromise = null;

export const recordUserActivity = () => {
  const now = Date.now();
  if (now - lastRecordedActivityTimestamp < ACTIVITY_THROTTLE_MILLISECONDS) {
    return false;
  }
  lastActivityTimestamp = now;
  lastRecordedActivityTimestamp = now;
  return true;
};

export const refreshAuthTokenIfNeeded = async () => {
  const authToken = loadAuthTokenFromLocalStorage();
  if (!authToken) {
    return null;
  }

  const now = Date.now();
  const tokenExpiration = getAuthTokenExpiration() * 1000;
  const userWasRecentlyActive = now - lastActivityTimestamp <= RECENT_ACTIVITY_WINDOW_MILLISECONDS;
  if (!userWasRecentlyActive || tokenExpiration - now > TOKEN_REFRESH_THRESHOLD_MILLISECONDS) {
    return authToken;
  }

  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = axios({
    method: "POST",
    url: "/api/v1/user/refresh-token",
    headers: { Authorization: `Bearer ${authToken}` },
  })
    .then((response) => {
      saveAuthTokenInLocalStorage(response.data.jwt);
      return response.data.jwt;
    })
    .catch((error) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearAuthToken();
      }
      throw error;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

export const isUserLoggedIn = () => {
  if (!userHasAuthToken()) {
    return false;
  }
  let tokenExpiryInMilli = getAuthTokenExpiration() * 1000;
  let currentTimeInMilli = new Date().getTime();

  if (tokenExpiryInMilli > currentTimeInMilli) {
    return true;
  } else {
    return false;
  }
};

export const getRealestateIdsAndNames = () => {
  // Don't use this method. Get this from the state in HomePage.js through useContext instead.
  if (!userHasAuthToken()) {
    return false;
  }
  return getRealestatesFromAuthToken();
};

export const userHasAuthToken = () => {
  let token = loadAuthTokenFromLocalStorage();
  return token !== undefined && token !== "undefined" && token !== null && token !== "null";
};

export const request = async (method, url, sendToken, data, { silent = false } = {}) => {
  let headers = {};
  if (userHasAuthToken() && sendToken) {
    recordUserActivity();
    const authToken = await refreshAuthTokenIfNeeded().catch(() => loadAuthTokenFromLocalStorage());
    headers = { Authorization: `Bearer ${authToken}` };
  }

  try {
    return await axios({
      method: method,
      headers: headers,
      url: url,
      data: data,
    });
  } catch (error) {
    const response = error.response;

    if (response?.status === 401) {
      clearAuthToken();
    }

    if (!silent) notification.error({
      message: response?.data?.errorType ?? "Request failed",
      description: (
        <div>
          <div>
            <text>{response?.data?.message ?? "An unexpected error occurred."}</text>
          </div>
          <br />
          <div>
            <text type="secondary" style={{ fontSize: 12 }}>
              Error ID: {response?.data?.requestId}
            </text>
          </div>
        </div>
      ),
      placement: "top",
    });

    return error.response;
  }
};
